import { useCallback, useEffect, useState } from "react"
import { Erfassung } from "./Erfassung"
import { Sperre } from "./Sperre"
import { antwortDetails, setzeSitzung, terminalApi, type TerminalStatus } from "@/lib/terminalApi"

type Zustand =
  | { art: "laden" }
  | { art: "gesperrt"; status: TerminalStatus; hinweis?: Hinweis }
  | { art: "angemeldet"; status: TerminalStatus; name: string; sekunden: number }

export interface Hinweis {
  text: string
  chipnummer?: string
  ton?: "info" | "fehler"
}

/**
 * Terminal an der Anlage. Frei aufrufbar bis zum Sperrbildschirm „Chip an den Leser
 * halten“; erst der Chip meldet an → Erfassung → nach dem Speichern oder bei Leerlauf
 * zurück zum Sperrbildschirm.
 */
export function TerminalApp() {
  const [zustand, setZustand] = useState<Zustand>({ art: "laden" })
  useEffect(() => {
    document.title = "Betriebstagebuch"
  }, [])

  const statusLaden = useCallback(async (hinweis?: Hinweis) => {
    setzeSitzung(null)
    try {
      const status = (await terminalApi.get<TerminalStatus>("/status")).data
      setZustand({ art: "gesperrt", status, hinweis })
    } catch (e) {
      // Netz weg: in 5 s erneut versuchen, bis dahin den letzten Stand zeigen
      const d = antwortDetails(e)
      window.setTimeout(() => void statusLaden(), 5000)
      setZustand((z) => (z.art === "gesperrt" ? { ...z, hinweis: { text: d.detail, ton: "fehler" } } : { art: "laden" }))
    }
  }, [])

  useEffect(() => {
    void statusLaden()
  }, [statusLaden])

  const abmelden = useCallback(
    async (hinweis?: Hinweis) => {
      await terminalApi.post("/abmelden").catch(() => undefined)
      await statusLaden(hinweis)
    },
    [statusLaden],
  )

  if (zustand.art === "laden") {
    return <main className="flex min-h-screen items-center justify-center bg-page text-xl text-ink2">Terminal wird gestartet …</main>
  }
  if (zustand.art === "gesperrt") {
    return (
      <Sperre
        status={zustand.status}
        hinweis={zustand.hinweis}
        onAngemeldet={(name, sekunden) => setZustand({ art: "angemeldet", status: zustand.status, name, sekunden })}
        onHinweis={(hinweis) => setZustand({ ...zustand, hinweis })}
      />
    )
  }
  return (
    <Erfassung
      status={zustand.status}
      name={zustand.name}
      sitzungSekunden={zustand.sekunden}
      onAbmelden={(hinweis) => void abmelden(hinweis)}
    />
  )
}
